import React, { useState } from "react";
import { Button, buttonClasses, styled, Menu, MenuItem, Typography } from "@mui/material";
import FilterIcon from "src/icons/FilterIcon";
import Tooltip from "@hyro-components/Tooltip";
import AddIcon from "src/icons/AddIcon";
import { BUTTON_DISPLAY_MODES } from "../../HyroFilter.consts";

const BUTTON_SIZE = "36px";

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "displayMode"
})(({ theme, disabled, displayMode }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.75),
  height: BUTTON_SIZE,
  [`& .${buttonClasses.startIcon}`]: {
    margin: 0
  },
  ...(displayMode === BUTTON_DISPLAY_MODES.ICON && {
    backgroundColor: theme.palette.system[0],
    minWidth: BUTTON_SIZE,
    width: BUTTON_SIZE,
    color: theme.palette.system[700],
    border: `1px solid ${disabled ? theme.palette.system[200] : theme.palette.system[400]}`,
    "& svg path": {
      stroke: "currentColor"
    },
    [`&.${buttonClasses.disabled} svg path`]: {
      stroke: theme.palette.system[400]
    },
    "&:hover, &.active": {
      color: theme.palette.primary[500],
      backgroundColor: theme.palette.tertiary[100],
      borderColor: theme.palette.secondary[500],
    }
  }),
  ...(displayMode === BUTTON_DISPLAY_MODES.ICON_TEXT && {
    height: "32px",
    paddingLeft: theme.spacing(1.35),
    color: theme.palette.primary[500],
    "& svg path": {
      stroke: "currentColor"
    },
    [`&.${buttonClasses.disabled} svg path`]: {
      stroke: theme.palette.system[400]
    },
    "&:hover, &.active": {
      backgroundColor: theme.palette.tertiary[100]
    }
  })
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  textOverflow: "ellipsis",
  height: "40px"
}));

const FilterButton = ({
  displayMode = BUTTON_DISPLAY_MODES.ICON,
  filterOptions = [],
  testId,
  disabled,
  onFilterOptionClick
}) => {
  const startIcon = displayMode === BUTTON_DISPLAY_MODES.ICON ? <FilterIcon /> : <AddIcon strokeWidth={1.4} />;
  const text = displayMode === BUTTON_DISPLAY_MODES.ICON_TEXT ? "Add filter" : "";
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const isDisabled = disabled || filterOptions.length === 0;
  const tooltipText = filterOptions.length === 0 ? "All filters are already applied" : displayMode === BUTTON_DISPLAY_MODES.ICON && !disabled ? "Filter" : "";

  const handleFilterOptionClick = (filterKey) => {
    onFilterOptionClick(filterKey);
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={tooltipText} testId={`${testId}-filter-button-tooltip`} tooltipStyle={{ maxWidth: "97px" }}>
        <StyledButton
          data-testid={`${testId}-filter-button`}
          className={isMenuOpen ? "active" : ""}
          disabled={isDisabled}
          displayMode={displayMode}
          startIcon={startIcon}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {text}
        </StyledButton>
      </Tooltip>

      <Menu
        data-testid={`${testId}-filter-menu`}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          style: {
            width: "170px",
            maxHeight: "196px",
            marginTop: "4px",
            overflowY: "scroll"
          }
        }}
      >
        {filterOptions.map((option, index) => (
          <StyledMenuItem key={option.key} data-testid={`${testId}-filter-menu-item-${index}`} onClick={() => handleFilterOptionClick(option.key)}>
            <Typography variant="body2" noWrap>{option.label}</Typography>
          </StyledMenuItem>
        ))}
      </Menu>
    </>
  );
};

export default FilterButton;
